import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { parseSheet, downloadTemplate } from "@/lib/importer";
import { inr, fmtDate } from "@/lib/api";

const today = () => new Date().toLocaleDateString("en-CA");

export const ImportDialog = ({ open, onOpenChange, onImport }) => {
  const [date, setDate] = useState(today());
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setRes(null); setDate(today()); } }, [open]);
  useEffect(() => { if (file) parseSheet(file, date).then(setRes).catch(() => setRes({ rows: [], errors: ["Could not read file"] })); }, [file, date]);

  const submit = async () => { setBusy(true); await onImport(res.rows.map(({ key, ...r }) => r)); setBusy(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="import-dialog" className="sm:max-w-2xl" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-head text-2xl">Import from Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          Columns: <span className="font-mono text-xs">Group Name, Site Name, User ID, Date, Settlement Amount</span>. New groups and sites are created automatically. Leave amount empty to add sites only.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[200px] text-xs text-slate-500 space-y-1">
            <span>File (.xlsx, .xls, .csv)</span>
            <Input data-testid="import-file-input" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
          <label className="text-xs text-slate-500 space-y-1">
            <span>Date if missing in sheet</span>
            <Input data-testid="import-default-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
          </label>
          <Button variant="outline" data-testid="import-template-btn" onClick={downloadTemplate}><FileSpreadsheet className="w-4 h-4 mr-2" />Template</Button>
        </div>
        {res && (
          <div className="space-y-2">
            <p data-testid="import-summary" className="text-sm"><b>{res.rows.length}</b> valid rows{res.errors.length > 0 && <span className="text-red-600"> · {res.errors.length} skipped</span>}</p>
            {res.errors.length > 0 && (
              <ul data-testid="import-errors" className="text-xs text-red-600 max-h-20 overflow-y-auto">{res.errors.map((e) => <li key={e}>{e}</li>)}</ul>
            )}
            <div className="border rounded-lg max-h-56 overflow-auto">
              <table data-testid="import-preview" className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-500">
                  {["Group", "Site", "User ID", "Date", "Amount"].map((h) => <th key={h} className="py-2 px-3 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>{res.rows.slice(0, 100).map((r) => (
                  <tr key={r.key} className="border-t">
                    <td className="py-1.5 px-3">{r.group_name}</td><td className="py-1.5 px-3">{r.site_name}</td>
                    <td className="py-1.5 px-3 font-mono">{r.user_code}</td><td className="py-1.5 px-3 font-mono">{fmtDate(r.entry_date) || "—"}</td>
                    <td className="py-1.5 px-3 font-mono text-right">{r.amount === null ? "—" : inr(r.amount)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="import-submit-btn" disabled={!res?.rows.length || busy} onClick={submit} className="bg-sky-600 hover:bg-sky-700">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Import {res?.rows.length || 0} rows
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
